/**
 * Property-level view of the representor requirement (COL-466 follow-up):
 * which of a property's apartments would print no "vertreten durch" on their
 * contracts. Backs the property sidebar's Representor row, the "No
 * representor" badge on the Apartments tab's cards, and PropertyEditSheet's
 * save check — one resolver, so the three can't disagree.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  apartmentContractRepresentorName,
  useApartments,
} from "@/hooks/v2/useProperties";
import { useAuth } from "@/contexts/AuthContext";
import type { ApartmentListItem, Lessor, Property } from "@/types/property";

export type OwnerLink = { user_id: string; lessor_id: string | null };

/**
 * This property's property_owners rows, which create-contract reads for two
 * links (supabase/functions/create-contract/index.ts):
 *   • an apartment named only by `owner_user_id` gets the lessor of THAT
 *     owner's row for THIS property — not the app-wide owner→lessor map
 *     (useLessorIdByOwnerUser), which can pick another property's link or an
 *     email match the contract never uses;
 *   • a property with no `lessor_id` falls back to its first row, preferring
 *     one linked to a lessor ("Property-level owner" block).
 * Fetched in the edge function's own order, so rows[0] is its pick.
 */
function usePropertyOwnerLinks(propertyId: number | null) {
  return useQuery({
    queryKey: ["v2", "property-owner-links", propertyId],
    enabled: propertyId != null,
    queryFn: async (): Promise<OwnerLink[]> => {
      const { data, error } = await supabase
        .from("property_owners")
        .select("user_id, lessor_id, created_at")
        .eq("property_id", propertyId)
        .order("lessor_id", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as OwnerLink[];
    },
  });
}

/**
 * Only the lessors the chain can reach, fetched by id like create-contract
 * does — the unpaginated lessors list is capped at PostgREST's max-rows, and a
 * lessor past the cap would read as "no representor". Keyed under
 * ["v2", "lessors"] so every lessor write that refreshes that prefix
 * refreshes this too.
 */
function useLessorsById(ids: string[]) {
  return useQuery({
    queryKey: ["v2", "lessors", "by-id", ...ids],
    enabled: ids.length > 0,
    queryFn: async (): Promise<Lessor[]> => {
      const { data, error } = await supabase
        .from("lessors")
        .select("id, rep_name, rep_role, rep_note")
        .in("id", ids);
      if (error) throw error;
      return (data ?? []) as Lessor[];
    },
  });
}

/** owner user → lessor for this property, as create-contract's
 *  `.eq(user_id).eq(property_id).maybeSingle()` resolves it: a user with
 *  several rows here makes that lookup fail, so they get no entry. */
export function ownerLessorByUser(links: OwnerLink[]): Record<string, string> {
  const rowsPerUser = new Map<string, number>();
  for (const l of links) {
    rowsPerUser.set(l.user_id, (rowsPerUser.get(l.user_id) ?? 0) + 1);
  }
  const map: Record<string, string> = {};
  for (const l of links) {
    if (l.lessor_id && rowsPerUser.get(l.user_id) === 1) {
      map[l.user_id] = l.lessor_id;
    }
  }
  return map;
}

type QueryState<T> = {
  data: T | undefined;
  isPending: boolean;
  isError: boolean;
};

/**
 * The lessor ids the chain can reach: each apartment's own owner, the
 * property's lessor, and — for a property without one — its property_owners
 * fallback. Sorted so the query key is stable.
 */
export function representorLessorIds(
  apartments: ApartmentListItem[],
  property: Property,
  links: OwnerLink[]
): string[] {
  const ownerMap = ownerLessorByUser(links);
  const ids = new Set<string>();
  let someUnowned = false;
  for (const a of apartments) {
    const own =
      a.lessor_id ?? (a.owner_user_id ? ownerMap[a.owner_user_id] : null);
    if (own) ids.add(own);
    else someUnowned = true;
  }
  if (property.lessor_id) ids.add(property.lessor_id);
  else if (someUnowned && links[0]?.lessor_id) ids.add(links[0].lessor_id);
  return [...ids].sort();
}

export type RepresentorGapsResult = {
  /** Apartments that resolve no representor. Empty unless settled. */
  missing: ApartmentListItem[];
  total: number;
  /**
   * A lookup this answer depends on is still loading (`pending`) or failed
   * (`failed`). Either way an empty reading isn't a real "no representor", so
   * callers must not flag on it; PropertyEditSheet holds Save instead (same
   * rule as ApartmentEditSheet's repLookupPending). Never set when the typed
   * override or the legacy column already covers every apartment.
   */
  pending: boolean;
  failed: boolean;
  /**
   * The name each apartment's contracts would print (null = none), for
   * surfaces that display it. null until every lookup has settled, and while
   * some apartment can't be checked by this viewer.
   */
  names: Map<number, string | null> | null;
};

/**
 * Pure core of usePropertyRepresentorGaps. `ownerLinks` is null when this
 * viewer can't read property_owners (its RLS is owner-or-staff; an operator
 * account gets an empty result, not an error): apartments whose chain runs
 * through those rows are then neither flagged nor named, rather than reported
 * as missing on data the viewer was never shown.
 */
export function computeRepresentorGaps(input: {
  property: Property;
  apartments: QueryState<ApartmentListItem[]>;
  ownerLinks: QueryState<OwnerLink[]> | null;
  lessors: QueryState<Lessor[]>;
}): RepresentorGapsResult {
  const { property, apartments, ownerLinks, lessors } = input;
  const apts = apartments.data ?? [];
  // A property-level override or the legacy flat column covers every
  // apartment the chain doesn't resolve earlier, so nothing can be missing.
  const covered = !!(
    property.rep_override?.name?.trim() || property.representor_name?.trim()
  );
  const base: RepresentorGapsResult = {
    missing: [],
    total: apts.length,
    pending: false,
    failed: false,
    names: null,
  };
  // isPending, not isLoading: a paused (offline) query is pending with no
  // data but not "loading", and must not read as an empty answer either.
  const unsettled = (q: QueryState<unknown>) => q.isPending || q.isError;
  const stall = (q: QueryState<unknown>): RepresentorGapsResult =>
    covered ? base : { ...base, pending: !q.isError, failed: q.isError };

  if (unsettled(apartments)) return stall(apartments);

  // Only the lookups this property's apartments actually consult count.
  // create-contract reads property_owners only for an apartment without a
  // lessor_id of its own — to bridge its owner account, or to fall back to
  // the property's owner when the property has no lessor either — so a
  // failed owner-link query must not hold up any other property.
  const readsOwnerLinks = (a: ApartmentListItem) =>
    !a.lessor_id && (!!a.owner_user_id || !property.lessor_id);
  if (ownerLinks && apts.some(readsOwnerLinks) && unsettled(ownerLinks)) {
    return stall(ownerLinks);
  }
  const links = ownerLinks?.data ?? [];
  if (
    representorLessorIds(apts, property, links).length > 0 &&
    unsettled(lessors)
  ) {
    return stall(lessors);
  }

  const ownerMap = ownerLessorByUser(links);
  const lessorById = new Map((lessors.data ?? []).map((l) => [l.id, l]));
  const propertyOwnerLessorId = property.lessor_id
    ? null
    : (links[0]?.lessor_id ?? null);
  const ctx = {
    lessorById,
    propertyLessor: property.lessor_id
      ? (lessorById.get(property.lessor_id) ?? null)
      : null,
    propertyOwnerLessor: propertyOwnerLessorId
      ? (lessorById.get(propertyOwnerLessorId) ?? null)
      : null,
    lessorIdByOwnerUser: ownerMap,
  };
  // Without owner-link access, those same apartments can't be judged.
  const uncheckable = (a: ApartmentListItem) =>
    !ownerLinks && readsOwnerLinks(a);

  const names = new Map<number, string | null>();
  for (const a of apts) {
    if (!uncheckable(a)) {
      names.set(a.id, apartmentContractRepresentorName(a, property, ctx));
    }
  }
  return {
    missing: covered
      ? []
      : apts.filter((a) => names.has(a.id) && names.get(a.id) === null),
    // Only the apartments actually checked, so "Missing on N of M" never
    // implies the uncheckable ones are fine.
    total: names.size,
    pending: false,
    failed: false,
    names: names.size === apts.length ? names : null,
  };
}

export type PropertyRepresentorGaps = RepresentorGapsResult & {
  /** Refetch whichever lookup failed. */
  retry: () => void;
};

const DISABLED: PropertyRepresentorGaps = {
  missing: [],
  total: 0,
  pending: false,
  failed: false,
  names: null,
  retry: () => {},
};

export function usePropertyRepresentorGaps(
  property: Property | null | undefined,
  opts: {
    /**
     * The property-level override as currently typed (PropertyEditSheet), so
     * the answer reflects the save about to happen. Omit to use the stored
     * `property.rep_override`.
     */
    repOverrideName?: string;
    /** false for an owner-portal account: the requirement is staff-only. */
    enabled?: boolean;
  } = {}
): PropertyRepresentorGaps {
  const { repOverrideName, enabled = true } = opts;
  // property_owners is readable by staff (has_any_admin_role) or the owner
  // themselves; anyone else — an operator account — would get [] back.
  const { hasStaffRole } = useAuth();
  // Nothing is fetched while disabled.
  const propertyId = enabled ? (property?.id ?? null) : null;
  const apartmentsQ = useApartments(propertyId);
  const ownerLinksQ = usePropertyOwnerLinks(hasStaffRole ? propertyId : null);
  const lessorIds =
    enabled && property && apartmentsQ.data
      ? representorLessorIds(
          apartmentsQ.data,
          property,
          hasStaffRole ? (ownerLinksQ.data ?? []) : []
        )
      : [];
  const lessorsQ = useLessorsById(lessorIds);

  if (!enabled || !property) return DISABLED;

  const draft: Property =
    repOverrideName === undefined
      ? property
      : {
          ...property,
          rep_override: repOverrideName.trim()
            ? { name: repOverrideName.trim() }
            : null,
        };
  const result = computeRepresentorGaps({
    property: draft,
    apartments: apartmentsQ,
    ownerLinks: hasStaffRole ? ownerLinksQ : null,
    lessors: lessorsQ,
  });
  return {
    ...result,
    retry: () => {
      if (apartmentsQ.isError) void apartmentsQ.refetch();
      if (ownerLinksQ.isError) void ownerLinksQ.refetch();
      if (lessorsQ.isError) void lessorsQ.refetch();
    },
  };
}
